import type { Change, PreviewResponse, RuleGroupView, RulesView } from '@tabel/shared/schemas/rules';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Undo2 } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { errorText, useToast } from '../../components/ErrorToast';
import { Button, Callout, Card, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { DayRow } from './DaysDialog';
import type { OpenDay } from './ExampleDay';
import { shouldReport } from './LoadError';
import { ChecksList } from './RuleEditor';
import { DRAFT, LABEL, T, say } from './texts';

/*
 * Вид «Что изменится» (004, T067; contracts/ui.md, «Вид «Что изменится»»; FR-029): четыре блока — изменения словами
 * с «Отменить изменение» (только ведущему), дни, в которых изменятся часы (строка открывает расчёт дня), «Дни с
 * правкой», подписанные табели; ниже — предупреждения и ошибки черновика. Влияние всего черновика считает сервер с
 * даты начала по умолчанию (`POST /api/rules/draft/preview`, FR-035); пока считает — `computing`, интерфейс
 * спрашивает снова.
 */

const POLL_MS = 1000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Влияние черновика: запрос и ожидание, пока сервер досчитает; устарело — посчитать заново. */
export async function loadPreview(etag: string, validFrom: string): Promise<PreviewResponse> {
  let res = await api.preview({ etag, validFrom });
  for (let i = 0; i < 120 && res.status === 'computing'; i++) {
    await sleep(POLL_MS);
    res = await api.previewGet(res.previewId);
    if (res.status === 'stale') res = await api.preview({ etag, validFrom });
  }
  return res;
}

function Block({ label, children, aside }: { label: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <Card>
      <section aria-label={label} className="px-5 py-4 max-sm:px-3.5">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h2 className="text-[15px] font-semibold text-ink">{label}</h2>
          {aside}
        </div>
        {children}
      </section>
    </Card>
  );
}

function RevertGroup({ etag, group }: { etag: string; group: string }) {
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const revert = useMutation({
    mutationFn: () => api.revert({ etag, group }),
    onSuccess: () => {
      showInfo(DRAFT.reverted);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
    onError: (e) => {
      showError(e, { screen: 'Rules', action: 'revert change', retry: () => revert.mutate() });
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
  });
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={Undo2}
      disabled={revert.isPending}
      onClick={() => revert.mutate()}
    >
      {DRAFT.revert}
    </Button>
  );
}

export function ChangesView({
  view,
  onOpenDay,
  onRule,
}: {
  view: RulesView;
  onOpenDay: (day: OpenDay) => void;
  /** Ссылка на правило: правила — на вкладке «Правила». */
  onRule?: () => void;
}) {
  const { role } = useRole();
  const draft = view.draft ?? null;
  const leads = draft?.isLeader === true && role === 'rules_admin';
  const byId = useMemo(
    () => new Map<string, RuleGroupView>(view.groups.map((g) => [g.id, g])),
    [view.groups],
  );

  /** Изменения по правилам — в порядке правил страницы. */
  const groups = useMemo(() => {
    const by = new Map<string, Change[]>();
    for (const c of draft?.changes ?? []) by.set(c.group, [...(by.get(c.group) ?? []), c]);
    const order = view.groups.map((g) => g.id);
    return [...by.entries()].sort(([a], [b]) => order.indexOf(a) - order.indexOf(b));
  }, [draft, view.groups]);

  const etag = draft?.etag ?? '';
  const validFrom = draft?.defaultValidFrom ?? '';
  const preview = useQuery({
    queryKey: rulesKeys.preview(role, etag, validFrom),
    queryFn: async () => {
      try {
        return await loadPreview(etag, validFrom);
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'draft preview', e);
        throw e;
      }
    },
    // Правила к запуску (007, FR-004a): у площадки нет табелей — дни до запуска не считаются.
    enabled: draft !== null && !view.launch,
    staleTime: 60_000,
  });

  if (!draft) return <p className="text-[13px] text-ink-2">{T.changesEmpty}</p>;

  const days = preview.data?.days ?? [];
  const changed = days.filter((d) => !d.edited);
  const edited = days.filter((d) => d.edited);
  const risks = [...draft.risks, ...(preview.data?.risks ?? [])].filter(
    (r, i, all) => all.findIndex((x) => x.code === r.code) === i,
  );
  const errors = [...draft.errors, ...(preview.data?.errors ?? [])].filter(
    (e, i, all) => all.findIndex((x) => x.code === e.code && x.field === e.field && x.cube === e.cube) === i,
  );
  const errorId = preview.error instanceof ApiError ? preview.error.requestId : null;
  const open = (d: (typeof days)[number]) => () =>
    onOpenDay({ personId: d.personId, name: d.name, date: d.date });

  return (
    <div className="space-y-4">
      <Block label={LABEL.changesList}>
        {groups.length === 0 ? (
          <p className="text-[13px] text-ink-2">{T.changesEmpty}</p>
        ) : (
          <ul className="divide-y divide-line">
            {groups.map(([id, list]) => (
              <li key={id} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1 basis-[260px]">
                  <a
                    href={`#${id}`}
                    onClick={onRule}
                    className="text-[13.5px] font-medium text-ink hover:text-accent hover:underline"
                  >
                    {byId.get(id)?.question ?? id}
                  </a>
                  <ul className="mt-0.5 space-y-0.5 text-[13px] text-ink-2">
                    {list.map((c) => (
                      <li key={c.field}>{c.text}</li>
                    ))}
                  </ul>
                </div>
                {leads && <RevertGroup etag={draft.etag} group={id} />}
              </li>
            ))}
          </ul>
        )}
      </Block>

      {view.launch && (
        // Правила к запуску (007, FR-004a): состояние раздела — подсказкой (дизайн-система Callout).
        <Callout tone="accent" icon={Info}>
          {T.launchNoDays}
        </Callout>
      )}
      {preview.isLoading && (
        <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
          <Skeleton className="h-32 w-full rounded-card" />
          <Skeleton className="h-20 w-full rounded-card" />
        </div>
      )}
      {preview.error && (
        <Card>
          <div role="alert" className="flex flex-wrap items-center gap-3 px-5 py-4 text-[13px] max-sm:px-3.5">
            <p className="min-w-0 flex-1 text-bad">
              {`${T.previewError} ${errorText(preview.error, '')}`.trim()}
              {errorId && <span className="block text-[12px] text-ink-3">{say.errorCode(errorId)}</span>}
            </p>
            <Button size="sm" onClick={() => void preview.refetch()}>
              {LABEL.retry}
            </Button>
          </div>
        </Card>
      )}
      {preview.data && (
        <>
          <Block
            label={LABEL.changedDays}
            aside={<span className="text-[12.5px] text-ink-3">{say.previewFrom(validFrom)}</span>}
          >
            {changed.length === 0 ? (
              <p className="text-[13px] text-ink-2">{T.noDayChanges}</p>
            ) : (
              <ul aria-label={LABEL.changedDays} className="-mx-3 max-h-[480px] overflow-y-auto">
                {changed.map((d) => (
                  <DayRow key={`${d.personId}|${d.date}`} day={d} onOpen={open(d)} />
                ))}
              </ul>
            )}
          </Block>
          <Block label={T.editedDaysTitle}>
            {edited.length === 0 ? (
              <p className="text-[13px] text-ink-2">{T.noEditedDays}</p>
            ) : (
              <ul aria-label={T.editedDaysTitle} className="-mx-3 max-h-[320px] overflow-y-auto">
                {edited.map((d) => (
                  <DayRow key={`${d.personId}|${d.date}`} day={d} onOpen={open(d)} />
                ))}
              </ul>
            )}
          </Block>
          {(preview.data.summary?.signedDays ?? 0) > 0 && (
            <Card>
              <p className="px-5 py-4 text-[13.5px] text-ink-2 max-sm:px-3.5">
                {say.signedDays(preview.data.summary?.signedDays ?? 0)}
              </p>
            </Card>
          )}
        </>
      )}
      {(risks.length > 0 || errors.length > 0) && (
        <section aria-label={LABEL.warnings} className="space-y-1.5">
          <h2 className="text-[12px] font-semibold tracking-[0.07em] text-ink-3 uppercase">
            {LABEL.warnings}
          </h2>
          <ChecksList errors={errors} risks={risks} byId={byId} onRule={onRule} />
        </section>
      )}
    </div>
  );
}
